// ============================================================================
// Authentication Module - Supabase Integration
// ============================================================================

import { createClient } from '@supabase/supabase-js';
import { notificationSubscriptionService } from 'shared/services/notification-subscription-service.js';
import { sidebar } from 'app/shell/sidebar.js';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from 'config/supabase.js';

// Absolute on purpose: a signed-out visit to a nested path (a shared link
// without the hash) used to resolve this relative to that path and redirect
// to .../lists/src/features/login/... forever.
const AUTH_ROUTES = {
    login: '/src/features/login/login.html'
};

// Session auto-logout after 12 hours
const SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const SESSION_START_KEY = 'aerium_session_started_at';
const SESSION_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

// Profile fields to fetch (avoid SELECT *)
const PROFILE_FIELDS = 'user_id, full_name, short_name, avatar_url, role, display_title, can_approve_leave, can_manage_leave, can_view_leave, can_review_budgets, can_sign_off_budgets, can_view_amounts, can_approve_billing, receives_billing_emails, is_partner, list_page_size, pinned_task_lists, sidebar_config, text_expansions, show_on_dashboard, timesheet_required, timesheet_start_date, target_weekly_hours, is_timesheet_manager';

// Pre-compiled email regex (avoid recompilation on each validation)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Initialize Supabase client
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
    }
});

export const auth = {
    user: null,
    profile: null,
    _loadProfilePromise: null,
    _authSubscription: null,
    _sessionCheckInterval: null,
    _visibilityHandler: null,

    // Load user profile data (with request deduplication)
    async loadProfile() {
        if (!this.user) return null;
        if (this._loadProfilePromise) return this._loadProfilePromise;

        this._loadProfilePromise = supabase
            .from('profiles')
            .select(PROFILE_FIELDS)
            .eq('user_id', this.user.id)
            .maybeSingle()
            .then(({ data, error }) => {
                if (error) console.error('[Auth] Failed to load profile:', error);
                this.profile = error ? null : data;
                return this.profile;
            })
            .catch(error => {
                console.error('[Auth] Error loading profile:', error);
                return null;
            })
            .finally(() => { this._loadProfilePromise = null; });

        return this._loadProfilePromise;
    },

    // Check if user is authenticated (returns false if profile fails to load or session expired)
    async check() {
        try {
            const { data: { session }, error } = await supabase.auth.getSession();
            if (error) throw error;
            if (!session) return false;

            // Check 12-hour session limit
            if (this._isSessionExpired()) {
                console.log('[Auth] Session expired after 12 hours');
                await this.signOut();
                return false;
            }

            this.user = session.user;
            await this.loadProfile();

            if (!this.profile) {
                console.error('[Auth] Profile load failed - denying authentication');
                this.user = null;
                return false;
            }

            // Ensure session start is tracked (handles existing sessions on first load after update)
            if (!localStorage.getItem(SESSION_START_KEY)) {
                localStorage.setItem(SESSION_START_KEY, Date.now().toString());
            }

            return true;
        } catch (error) {
            console.error('[Auth] Check failed:', error);
            return false;
        }
    },

    // Sign in with validation
    async signIn(email, password) {
        if (!email || !password) return { success: false, error: 'Email and password are required' };
        if (!EMAIL_REGEX.test(email)) return { success: false, error: 'Invalid email format' };

        try {
            const { data, error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) throw error;

            this.user = data.user;
            await this.loadProfile();
            localStorage.setItem(SESSION_START_KEY, Date.now().toString());
            return { success: true };
        } catch (error) {
            console.error('[Auth] Sign in failed:', error);
            return { success: false, error: error.message };
        }
    },

    // Send password reset email
    async resetPassword(email) {
        if (!email || !EMAIL_REGEX.test(email)) return { success: false, error: 'Valid email is required' };

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}${AUTH_ROUTES.login}`
            });
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('[Auth] Password reset failed:', error);
            return { success: false, error: error.message };
        }
    },

    // Sign out - always redirects to login, even on error
    async signOut() {
        try {
            this._stopSessionMonitor();
            notificationSubscriptionService.destroy();
            sidebar.dispose();
            this._authSubscription?.data?.subscription?.unsubscribe();

            const { error } = await supabase.auth.signOut();
            if (error) console.error('[Auth] Sign out API error:', error);
        } catch (error) {
            console.error('[Auth] Sign out failed:', error);
        } finally {
            this.user = null;
            this.profile = null;
            this._authSubscription = null;
            localStorage.removeItem(SESSION_START_KEY);
            window.location.href = AUTH_ROUTES.login;
        }
    },

    // Listen for auth changes (stores subscription for cleanup)
    onAuthChange(callback) {
        this._authSubscription = supabase.auth.onAuthStateChange((event, session) => {
            this.user = session?.user ?? null;
            this.profile = this.user ? this.profile : null;
            if (this.user) this.loadProfile();
            callback(event, session);
        });
        return this._authSubscription;
    },

    // Start periodic session expiry check (call after successful auth)
    startSessionMonitor() {
        this._stopSessionMonitor();
        this._sessionCheckInterval = setInterval(() => this._checkSessionExpiry(), SESSION_CHECK_INTERVAL_MS);
        this._visibilityHandler = () => {
            if (document.visibilityState === 'visible') this._checkSessionExpiry();
        };
        document.addEventListener('visibilitychange', this._visibilityHandler);
    },

    _stopSessionMonitor() {
        if (this._sessionCheckInterval) {
            clearInterval(this._sessionCheckInterval);
            this._sessionCheckInterval = null;
        }
        if (this._visibilityHandler) {
            document.removeEventListener('visibilitychange', this._visibilityHandler);
            this._visibilityHandler = null;
        }
    },

    _isSessionExpired() {
        const startedAt = localStorage.getItem(SESSION_START_KEY);
        if (!startedAt) return false;
        return (Date.now() - Number(startedAt)) >= SESSION_MAX_AGE_MS;
    },

    _checkSessionExpiry() {
        if (this._isSessionExpired()) {
            console.log('[Auth] Session expired after 12 hours - auto logout');
            this.signOut();
        }
    }
};
// Export supabase for database operations
export { supabase };
